<template>
    <app-layout>
        <base-header :back-url="route('stores.budget-projections.select')" :shadow="false" border
                     border-bg-from="from-yellow-500"
                     border-bg-to="to-orange-500"
        >
            <div class="flex items-center justify-between w-full">
                <div>
                    <h4 class="text-xl font-medium">
                        {{ $t('general.budget-projections') }}
                    </h4>

                    <span>
                        {{ store.name }}
                    </span>
                </div>

                <div class="flex items-center">
                    <div>
                        <default-button
                            :loading="isLoading"
                            :disabled="!!currentBudgetProjection"
                            icon="plus"
                            size="sm"
                            @click.native="createBudgetProjection"
                        >
                            {{ $t('budget-projection.create') }}
                        </default-button>

                        <span v-if="!isLoading && !!currentBudgetProjection" class="text-xs">
                            {{ $t('budget-projection.already-exists-for-current-year') }}
                        </span>
                    </div>
                </div>
            </div>
        </base-header>

        <table-base :busy="isLoading" class="text-sm">
            <template #head>
                <table-head :sort-query="sortQuery" sort="year">{{ $t('date.year') }}</table-head>
                <table-head :sort-query="sortQuery" sort="status">{{ $t('general.status') }}</table-head>
                <table-head></table-head>
            </template>
            <template #body>
                <table-row v-for="budgetProjection in budgetProjections.data" :key="budgetProjection.id">
                    <table-cell>
                        {{ budgetProjection.year }}
                    </table-cell>
                    <table-cell>
                        <status-badge :budget="budgetProjection"/>
                    </table-cell>
                    <table-cell>
                        <action-bar>
                            <lock-button
                                v-if="auth.user.is_admin && !budgetProjection.is_in_review"
                                :is-locked="budgetProjection.is_locked"
                                :loading="isLocking.includes(budgetProjection.id)"
                                @click.native="toggleLock(budgetProjection)"
                            />

                            <span
                                v-else
                                :class="[budgetProjection.is_locked || budgetProjection.is_in_review ? 'text-red-700 dark:text-red-500' : 'text-green-700 dark:text-green-500']"
                            >
                                <icon :name="budgetProjection.is_locked ? 'lock-closed' : 'lock-open'"/>
                            </span>

                            <default-button
                                :href="route('stores.budget-projections.edit', [store.id, budgetProjection.id])"
                                color="blue" icon="clipboard-list"
                                size="xs"
                            >
                                {{ $t('general.entries') }}
                            </default-button>
                            <delete-button :loading="isDeleting.includes(budgetProjection.id)"
                                           :modal-title="$t('budget.delete')"
                                           @delete="deleteBudgetProjection(budgetProjection)"
                            >
                                <template #modal-body>
                                    {{ $t('budget.delete_confirm') }}
                                </template>
                            </delete-button>
                        </action-bar>
                    </table-cell>
                </table-row>
            </template>
        </table-base>
        <table-pagination v-if="budgetProjections && budgetProjections.meta" :links="budgetProjections.links"
                          :meta="budgetProjections.meta.pagination"
                          @paginate="loadPage"
        />
    </app-layout>
</template>

<script>
import Store from '@/Models/Store';
import StatusBadge from '@/Pages/Stores/BudgetProjections/Components/StatusBadge';
import LockButton from '@/Pages/Stores/Components/LockButton';
import BudgetProjection from '@/Models/BudgetProjection';
import moment from 'moment';

export default {
    components: {StatusBadge, LockButton},
    props: {
        user: Object,
        store: Store,
    },

    data: function() {
        return {
            budgetProjections: {},
            isLoading: true,
            isDeleting: [],
            isLocking: [],
            page: 1,
            sortQuery: ['-year'],
            year: null,
            currentBudgetProjection: null,
        };
    },

    metaInfo() {
        return {
            title: this.$t('general.budget-projections'),
        };
    },

    async created() {
        this.stores = await Store.$all();
        let currentBudgetProjection = await BudgetProjection
            .where('store_id', this.store.id)
            .where('year', moment().year())
            .$first();

        if (!currentBudgetProjection.year) {
            this.currentBudgetProjection = null;
        } else {
            this.currentBudgetProjection = currentBudgetProjection;
        }

        const urlParams = this.$helper.url().parameters();

        if (urlParams.has('page')) {
            this.page = urlParams.get('page') ?? 1;
        }

        if (urlParams.has('sort')) {
            this.sortQuery = urlParams.get('sort').split(',');
        }

        if (urlParams.has('year')) {
            this.year = parseInt(urlParams.get('year'));
        }

        await this.loadPage(parseInt(this.page));
    },

    methods: {
        toggleLock(budget) {
            this.isLocking.push(budget.id);

            axios.post(route('api.budget-projections.lock', [budget.id])).then(() => {
                this.applyQuery();
                this.isLocking.splice(this.isLocking.findIndex(id => id === budget.id), 1);
            });
        },

        async createBudgetProjection() {
            let budgetProjection = new BudgetProjection({year: null}).for(this.store);
            let currentBudgetProjection = await budgetProjection.save();

            if (currentBudgetProjection.year) {
                this.currentBudgetProjection = currentBudgetProjection;
            }

            await this.applyQuery();
        },

        async deleteBudgetProjection(budgetProjection) {
            if (this.currentBudgetProjection && this.currentBudgetProjection.year === budgetProjection.year) {
                this.currentBudgetProjection = null;
            }

            this.isDeleting.push(budgetProjection.id);
            await budgetProjection.delete();
            this.isDeleting = this.isDeleting.filter(item => item !== budgetProjection.id);
            await this.applyQuery();
        },

        async loadPage(page) {
            this.page = page;
            await this.applyQuery();
        },

        async applyQuery() {
            this.isLoading = true;

            let query = BudgetProjection.page(this.page)
                .where('store_id', this.store.id)
                .limit(20)
                .orderBy(this.sortQuery);

            if (this.year) {
                query = query.where('year', this.year);
            }

            this.budgetProjections = await query.all();
            this.isLoading = false;
        },
    },

    watch: {
        sortQuery() {
            this.applyQuery();
        },
    },
};
</script>
